import { db } from '../config/firebase';
import { collection, addDoc, getDocs, serverTimestamp, orderBy, query, where, doc, updateDoc } from 'firebase/firestore';

const COMMENTS_COLLECTION = 'episodeComments';

/**
 * Creates a new comment for a campaign episode.
 * @param {object} commentData - The data for the new comment.
 * @param {string} commentData.episodeId - The ID of the campaign episode.
 * @param {string} commentData.userId - The ID of the user posting the comment.
 * @param {string} commentData.userName - The display name of the user.
 * @param {string} [commentData.userPhotoURL] - The photo URL of the user (for out-of-character comments).
 * @param {string} commentData.content - The content of the comment.
 * @param {boolean} commentData.isInCharacter - Whether the comment is in-character or out-of-character.
 * @param {string} [commentData.characterId] - The ID of the character (if in-character comment).
 * @param {string} [commentData.characterName] - The name of the character (if in-character comment).
 * @param {string} [commentData.characterImageUrl] - The image URL of the character (if in-character comment).
 * @returns {Promise<string>} The ID of the newly created comment.
 */
export const createComment = async (commentData) => {
  if (!commentData.episodeId || !commentData.userId || !commentData.content) {
    throw new Error('Episode ID, user ID, and content are required to create a comment.');
  }
    try {
    const docRef = await addDoc(collection(db, COMMENTS_COLLECTION), {
      ...commentData,
      reactions: {},
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error("Error creating comment: ", error);
    throw error;
  }
};

/**
 * Fetches all comments for a specific campaign episode.
 * @param {string} episodeId - The ID of the campaign episode.
 * @returns {Promise<Array<object>>} An array of comment objects, ordered by creation date (newest first).
 */
export const getCommentsByEpisodeId = async (episodeId) => {
  if (!episodeId) {
    throw new Error('Episode ID is required to fetch comments.');
  }
  
  try {    const q = query(
      collection(db, COMMENTS_COLLECTION), 
      where('episodeId', '==', episodeId),
      orderBy('createdAt', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const comments = [];
    querySnapshot.forEach((doc) => {
      comments.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort comments by creation date (newest first) on the client side
    comments.sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime; // Descending order (newest first)
    });
    
    return comments;
  } catch (error) {
    console.error("Error fetching comments: ", error);
    throw error;
  }
};

/**
 * Toggles an emoji reaction on a comment.
 * @param {string} commentId - The ID of the comment.
 * @param {string} emoji - The emoji to toggle.
 * @param {string} userId - The ID of the user reacting.
 * @param {string} userName - The display name of the user.
 * @returns {Promise<void>}
 */
export const toggleCommentReaction = async (commentId, emoji, userId, userName) => {
  if (!commentId || !emoji || !userId) {
    throw new Error('Comment ID, emoji, and user ID are required to toggle reaction.');
  }
  
  try {
    const commentRef = doc(db, COMMENTS_COLLECTION, commentId);
    
    // Get current comment data to check existing reactions
    const commentDoc = await getDocs(query(collection(db, COMMENTS_COLLECTION), where('__name__', '==', commentId)));
    let commentData = null;
    commentDoc.forEach((doc) => {
      commentData = doc.data();
    });
    
    if (!commentData) {
      throw new Error('Comment not found.');
    }
      const reactions = commentData.reactions || {};
    const emojiReactions = reactions[emoji] || [];
    
    const existingReactionIndex = emojiReactions.findIndex(reaction => reaction.userId === userId);
    
    if (existingReactionIndex >= 0) {
      const updatedReactions = emojiReactions.filter(reaction => reaction.userId !== userId);
      if (updatedReactions.length === 0) {
        delete reactions[emoji];
      } else {
        reactions[emoji] = updatedReactions;
      }
    } else {
      reactions[emoji] = [...emojiReactions, { userId, userName }];
    }
    
    await updateDoc(commentRef, { reactions });
  } catch (error) {
    console.error("Error toggling comment reaction: ", error);
    throw error;
  }
};
